import React, { useState } from "react";
import { 
  Sparkles, ShieldCheck, CheckCircle2, AlertCircle, HelpCircle, 
  ExternalLink, ArrowRight, RefreshCw, Info, Lock, ChevronRight, Check, XCircle
} from "lucide-react";
import { SeoBreadcrumbs } from "./seo/SeoBreadcrumbs";

interface HowSignalsWorkPageProps {
  onNavigate: (path: string) => void;
  onLogin: (openSignUp?: boolean) => void;
}

export default function HowSignalsWorkPage({ onNavigate, onLogin }: HowSignalsWorkPageProps) {
  const [selectedSignalTab, setSelectedSignalTab] = useState<"high" | "possible" | "unconfirmed" | "improbable" | "low">("high");

  const signalDetails = {
    high: {
      label: "High Signal",
      badgeColor: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
      icon: <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />,
      definition: "Direct, public evidence of an open role (active career page posting or verified job board listing).",
      whatWeChecked: "Official domain careers page, active job schema, public job board API verification.",
      evidenceExample: "Career page found at acmedental.com/careers with 'Dental Hygienist - Posted 3 days ago'.",
      actionRecommendation: "Apply directly via their career page or send a personalized introduction to the owner/manager mentioning the open position.",
      verifiability: "100% Verifiable via direct source link."
    },
    possible: {
      label: "Possible Signal",
      badgeColor: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30",
      icon: <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      definition: "An official careers page or application route exists on their website, but specific local roles are unconfirmed or updated recently.",
      whatWeChecked: "Subdomain/path scan for /careers, /jobs, /join-our-team, or application contact form.",
      evidenceExample: "Official website contains a '/careers' link with general talent intake form.",
      actionRecommendation: "Reach out via the career intake form or email the practice manager directly expressing interest in future or upcoming openings.",
      verifiability: "High — Domain verified."
    },
    unconfirmed: {
      label: "Unconfirmed Signal",
      badgeColor: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30",
      icon: <HelpCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />,
      definition: "Business is experiencing rapid operational growth (review velocity spikes, secondary location opening) but no explicit career page link was detected.",
      whatWeChecked: "Places API review velocity, WHOIS updates, state business registry modifications.",
      evidenceExample: "45 new 5-star Google reviews in 30 days and secondary clinic address added.",
      actionRecommendation: "Ideal candidate for proactive direct outreach. Call or email the manager politely asking if extra staff is needed to support their growth.",
      verifiability: "Medium — Commercial growth verified."
    },
    improbable: {
      label: "Improbable Signal",
      badgeColor: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30",
      icon: <AlertCircle className="w-5 h-5 text-slate-500 dark:text-slate-400" />,
      definition: "Single-location static website with zero career pages or recent operational updates.",
      whatWeChecked: "Static website homepage, basic Maps profile, zero hiring keywords in DOM.",
      evidenceExample: "Website last modified 3 years ago; no careers page or active hiring mentions.",
      actionRecommendation: "Save to list and recheck later or make a low-pressure inquiry if located conveniently near your home.",
      verifiability: "Low — Static web footprint."
    },
    low: {
      label: "Low Signal",
      badgeColor: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30",
      icon: <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400" />,
      definition: "No website found, inactive domain, or unverified contact route.",
      whatWeChecked: "Places API website field, DNS check, phone line availability.",
      evidenceExample: "Map listing has no official website URL registered.",
      actionRecommendation: "In-person walk-in inquiry or phone call during off-peak morning hours if geographically close.",
      verifiability: "None — Unverified online footprint."
    }
  };

  const currentDetail = signalDetails[selectedSignalTab];

  const faqItems = [
    {
      q: "What is a hiring signal on BizNearYou?",
      a: "A hiring signal is an objective piece of public evidence—such as an active careers page, job posting schema, review velocity surge, or new facility expansion—that suggests a local business may need staff."
    },
    {
      q: "Does a hiring signal guarantee that a business is hiring?",
      a: "No. BizNearYou is strictly conservative. We never guarantee hiring. Signals indicate operational evidence so you can evaluate whether the business is worth contacting directly."
    },
    {
      q: "Why does BizNearYou label results conservatively?",
      a: "Misleading job seekers with false hiring promises wastes time. By showing conservative labels (Possible, Unconfirmed, High) along with freshness dates, you can make informed decisions based on real evidence."
    },
    {
      q: "How often are signals re-checked?",
      a: "Our automated crawlers inspect official career URLs, domain updates, and public Map indicators on a rolling cycle. Every result card displays the exact time it was last checked (e.g., 'Checked 2 hours ago')."
    },
    {
      q: "What should I do if a signal is incorrect?",
      a: "Every result card includes a 'Report Signal Issue' option. User feedback is continuously cross-referenced to update our database and refine our scoring algorithms."
    }
  ];

  return (
    <div className="bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 min-h-screen py-8 px-4 sm:px-6 lg:px-8 font-sans">
      
      {/* Schema Injection */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "How BizNearYou Signals Work: Evidence & Verification Standards",
          "description": "Comprehensive explanation of BizNearYou hiring signals, conservative confidence labels, freshness tracking, and direct source link verification.",
          "publisher": {
            "@type": "Organization",
            "name": "BizNearYou"
          }
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.a
            }
          }))
        })}
      </script>

      <div className="max-w-5xl mx-auto space-y-10">
        
        {/* Breadcrumb Navigation */}
        <SeoBreadcrumbs
          items={[
            { label: "Home", path: "/" },
            { label: "Resources", path: "/docs" },
            { label: "How Signals Work", path: "/how-signals-work" }
          ]}
          onNavigate={onNavigate}
        />

        {/* Hero Banner */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 sm:p-12 shadow-sm space-y-6 text-left relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <ShieldCheck className="w-48 h-48 text-blue-600 dark:text-blue-400" />
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-xs font-bold uppercase tracking-wider">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Transparency & Verification Standards</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 dark:text-white leading-tight">
            How BizNearYou Signals Work
          </h1>

          <p className="text-slate-600 dark:text-slate-300 text-base sm:text-lg max-w-3xl leading-relaxed font-medium">
            We provide evidence-based commercial intelligence to help job seekers, recruiters, and sales teams evaluate nearby businesses. We never guess, fabricate listings, or guarantee employment.
          </p>

          {/* Quick Trust Highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-extrabold text-xs text-slate-900 dark:text-white">Public Evidence Only</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">Signals are based on real web DOM scans, Places API data, and WHOIS records.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-extrabold text-xs text-slate-900 dark:text-white">Conservative Labeling</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">If evidence is missing or uncertain, we label it clearly as Possible or Unconfirmed.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <RefreshCw className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5" />
              <div>
                <p className="font-extrabold text-xs text-slate-900 dark:text-white">Freshness Date Stamps</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">Every signal shows the exact date and time it was last checked.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Signal Matrix Breakdown */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-sm space-y-8 text-left">
          
          <div className="space-y-2">
            <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
              The 5 Signal Levels
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Click any signal tier below to review what we check, real evidence examples, and our recommended action:
            </p>
          </div>

          {/* Signal Level Buttons */}
          <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-800 pb-4">
            {(["high", "possible", "unconfirmed", "improbable", "low"] as const).map((key) => {
              const item = signalDetails[key];
              const isSelected = selectedSignalTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedSignalTab(key)}
                  className={`px-4 py-2.5 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-2 border ${
                    isSelected
                      ? "bg-blue-600 text-white border-blue-600 shadow-md"
                      : "bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Signal Detail View */}
          <div className="p-6 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-700 pb-4">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-lg text-xs font-black border ${currentDetail.badgeColor}`}>
                  {currentDetail.label}
                </span>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
                  {currentDetail.verifiability}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
              <div className="space-y-2">
                <p className="font-extrabold text-slate-900 dark:text-slate-100 uppercase tracking-wider text-[10px]">What This Means</p>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed font-medium">{currentDetail.definition}</p>
              </div>

              <div className="space-y-2">
                <p className="font-extrabold text-slate-900 dark:text-slate-100 uppercase tracking-wider text-[10px]">Data Sources Checked</p>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">{currentDetail.whatWeChecked}</p>
              </div>

              <div className="space-y-2">
                <p className="font-extrabold text-slate-900 dark:text-slate-100 uppercase tracking-wider text-[10px]">Evidence Example</p>
                <div className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 font-mono text-[11px] text-slate-800 dark:text-slate-200">
                  {currentDetail.evidenceExample}
                </div>
              </div>

              <div className="space-y-2">
                <p className="font-extrabold text-slate-900 dark:text-slate-100 uppercase tracking-wider text-[10px]">Recommended Action</p>
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed font-semibold">{currentDetail.actionRecommendation}</p>
              </div>
            </div>
          </div>

        </div>

        {/* Why BizNearYou Does Not Guarantee Jobs */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 shadow-sm space-y-6 text-left">
          <div className="inline-flex items-center gap-2 text-xs font-extrabold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 px-3 py-1 rounded-full border border-amber-200 dark:border-amber-800">
            <Info className="w-3.5 h-3.5" />
            <span>Why Conservative Data Matters</span>
          </div>

          <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            Why BizNearYou Never Guarantees Hiring
          </h2>

          <div className="prose dark:prose-invert max-w-none text-slate-600 dark:text-slate-300 text-sm leading-relaxed space-y-4">
            <p>
              Traditional job portals suffer from phantom job postings—expired requisitions that remain online for months to collect resumes.
            </p>
            <p>
              BizNearYou takes the opposite approach: we show you operating local small businesses, reveal public activity evidence (like careers pages or review velocity surges), and encourage direct outreach.
            </p>
            <p className="font-semibold text-slate-900 dark:text-slate-100">
              By reaching out to businesses directly, you connect with local employers who hire through direct contact rather than paid advertisements.
            </p>
          </div>
        </div>

        {/* Frequently Asked Questions */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 shadow-sm space-y-6 text-left">
          <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            Frequently Asked Questions About Signals
          </h2>

          <div className="space-y-4">
            {faqItems.map((item, idx) => (
              <div key={idx} className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 space-y-2">
                <h3 className="font-extrabold text-slate-900 dark:text-white text-base flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
                  {item.q}
                </h3>
                <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm leading-relaxed pl-6">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA Block */}
        <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-sm border border-slate-200 dark:border-slate-800">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 dark:text-white">
            Ready to find local business opportunities?
          </h2>
          <p className="text-slate-600 dark:text-slate-300 text-sm max-w-xl mx-auto leading-relaxed">
            Search nearby businesses, review public signals, save promising prospects, and organize your direct outreach pipeline.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <button
              onClick={() => onNavigate("/")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-6 py-3.5 rounded-xl shadow-xs transition cursor-pointer text-sm flex items-center gap-2"
            >
              <span>Start Local Search</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => onLogin(true)}
              className="bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-white border border-slate-300 dark:border-slate-700 font-extrabold px-6 py-3.5 rounded-xl shadow-xs transition cursor-pointer text-sm"
            >
              Create Free Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
